import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import axios from "axios";
import { toast } from '../../../components/toast';
import { APP_PATH } from "../../../api/backendPath";
import PhysioDashboardLayout from "../../../components/physio-dashboard-layout/PhysioDashboardLayout";
import styles from "../../../styles/__lab_appointment_detail.module.scss";
import { formatDateNumeric as tzDateNumeric, formatCustomInLocale as tzLocale} from "../../../utils/datetime";

const PhysioEarningDetail = ({ onLogout }) => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("id");
  const navigate = useNavigate();
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orderId) fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem("usr_1a2b3c");
      const response = await axios.get(`${process.env.REACT_APP_API_URL}${APP_PATH.physioOrders}/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response?.data?.status) {
        setOrderDetails(response.data.data);
      }
    } catch (e) {
      toast.error("Failed to load order details");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      return tzLocale(d, 'en-GB', undefined);
    }
    return tzDateNumeric(new Date(dateStr));
  };

  const formatTime = (time) => {
    if (!time) return '—';
    const parts = time.split(':');
    if (parts.length >= 2) {
      const h = parseInt(parts[0]);
      const m = parts[1];
      const period = h >= 12 ? 'PM' : 'AM';
      const dh = h === 0 ? 12 : h > 12 ? h - 12 : h;
      return `${dh}:${m} ${period}`;
    }
    return time;
  };

  if (loading) {
    return (
      <PhysioDashboardLayout onLogout={onLogout}>
        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading...</div>
      </PhysioDashboardLayout>
    );
  }

  if (!orderDetails) {
    return (
      <PhysioDashboardLayout onLogout={onLogout}>
        <div style={{ padding: '40px', textAlign: 'center', color: '#EF4444' }}>Order not found</div>
      </PhysioDashboardLayout>
    );
  }

  const services = orderDetails?.services || [];
  const currentSessionNum = orderDetails?.current_session_number || 1;
  const isRevisitOrder = currentSessionNum > 1;
  const sessionHistory = orderDetails?.session_history || [];
  const transportationFee = Number(orderDetails?.transportation_fee || 0);
  const adminFeePercentage = Number(orderDetails?.admin_fee_percentage || 0);

  // Commission rates: resolved from the admin commission config with the
  // physio center's provider-specific override taking precedence (returned by
  // the order-details API). Falls back to 0 when not provided — never hardcoded.
  const simpleRate = Number(orderDetails?.commission_rates?.simple_non_insured || 0);
  const specialRate = Number(orderDetails?.commission_rates?.special_non_insured || 0);

  // Build session-wise data for all sessions
  const allSessions = [];
  for (let sNum = 1; sNum <= currentSessionNum; sNum++) {
    const sessionSvcs = services.filter(s => (s.number_of_sessions || 1) >= sNum);
    const sessionSubtotal = sessionSvcs.reduce((sum, s) => sum + Number(s.price || 0), 0);
    const sessionHasHomeVisit = sessionSvcs.some(s => s.location === 'HOME_VISIT');
    const sessionTransportationFee = sessionHasHomeVisit ? transportationFee : 0;
    const sessionAdminFee = (sessionSubtotal + sessionTransportationFee) * adminFeePercentage / 100;

    const simpleSvcs = sessionSvcs.filter(s => !(s.category_name || '').toLowerCase().includes('special'));
    const specialSvcs = sessionSvcs.filter(s => (s.category_name || '').toLowerCase().includes('special'));
    const simpleComm = simpleSvcs.reduce((sum, s) => sum + Number(s.price || 0), 0) * simpleRate / 100;
    const specialComm = specialSvcs.reduce((sum, s) => sum + Number(s.price || 0), 0) * specialRate / 100;
    const totalComm = simpleComm + specialComm;
    const netEarnings = sessionSubtotal + sessionTransportationFee - totalComm;

    const historyEntry = sessionHistory.find(h => h.session_number === sNum);

    allSessions.push({
      sessionNumber: sNum,
      services: sessionSvcs,
      subtotal: sessionSubtotal,
      transportationFee: sessionTransportationFee,
      adminFee: sessionAdminFee,
      simpleServices: simpleSvcs,
      specialServices: specialSvcs,
      simpleCommission: simpleComm,
      specialCommission: specialComm,
      totalCommission: totalComm,
      netEarnings,
      totalPatientPays: sessionSubtotal + sessionTransportationFee + sessionAdminFee,
      therapistName: historyEntry?.therapist_name || orderDetails?.therapist?.name || null,
      scheduledDate: historyEntry?.scheduled_date || (sNum === currentSessionNum ? orderDetails?.scheduled_date : null),
      paymentMethod: historyEntry?.payment_method || (sNum === currentSessionNum ? orderDetails?.payment_method : null),
      paymentStatus: historyEntry?.payment_status || (sNum === currentSessionNum ? orderDetails?.payment_status : null),
    });
  }

  return (
    <PhysioDashboardLayout onLogout={onLogout}>
      <div className={styles["apt-page-header"]}>
        <button onClick={() => navigate("/physio_earning_payment")} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>
          <FaArrowLeft /> Earning Detail
        </button>
      </div>

      <div style={{ padding: '0 24px 24px' }}>
        {/* Booking Info */}
        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
              Booking: {orderDetails?.booking_number || `PT-${orderId}`}
            </h3>
            <span style={{
              padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
              background: orderDetails?.status === 'COMPLETED' ? '#d1fae5' : '#fef3c7',
              color: orderDetails?.status === 'COMPLETED' ? '#065f46' : '#92400e',
            }}>
              {orderDetails?.status?.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')}
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
            <div>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 4px' }}>Total Sessions</p>
              <p style={{ fontSize: '14px', fontWeight: 500, margin: 0 }}>{currentSessionNum}</p>
            </div>
            <div>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 4px' }}>Payment Method</p>
              <p style={{ fontSize: '14px', fontWeight: 500, margin: 0 }}>{orderDetails?.payment_method || '—'}</p>
            </div>
            <div>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 4px' }}>Payment Status</p>
              <p style={{ fontSize: '14px', fontWeight: 500, margin: 0, color: orderDetails?.payment_status === 'COMPLETED' ? '#065f46' : '#92400e' }}>
                {orderDetails?.payment_status === 'COMPLETED' || orderDetails?.payment_status === 'PAID' ? 'Paid' : 'Pending'}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 4px' }}>Total Net Earnings</p>
              <p style={{ fontSize: '14px', fontWeight: 700, margin: 0, color: '#099494' }}>IQD {allSessions.reduce((sum, s) => sum + s.netEarnings, 0).toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Session-wise breakdown */}
        {allSessions.map((session) => (
          <div key={session.sessionNumber} style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px', marginBottom: '16px' }}>
            {/* Session Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f3f4f6' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Session {session.sessionNumber}</h3>
              <span style={{ fontSize: '11px', color: '#065f46', backgroundColor: '#d1fae5', padding: '2px 8px', borderRadius: '4px' }}>Completed</span>
              {session.therapistName && <span style={{ fontSize: '12px', color: '#6b7280' }}>• {session.therapistName}</span>}
              {session.scheduledDate && <span style={{ fontSize: '12px', color: '#6b7280' }}>• {formatDate(session.scheduledDate)}</span>}
            </div>

            {/* Services Table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '8px 0', fontSize: '12px', color: '#6b7280' }}>Service Name</th>
                  <th style={{ textAlign: 'left', padding: '8px 0', fontSize: '12px', color: '#6b7280' }}>Category</th>
                  <th style={{ textAlign: 'left', padding: '8px 0', fontSize: '12px', color: '#6b7280' }}>Location</th>
                  <th style={{ textAlign: 'right', padding: '8px 0', fontSize: '12px', color: '#6b7280' }}>Price</th>
                  <th style={{ textAlign: 'right', padding: '8px 0', fontSize: '12px', color: '#6b7280' }}>Commission</th>
                </tr>
              </thead>
              <tbody>
                {session.services.map((svc, idx) => {
                  const isSpecial = (svc.category_name || '').toLowerCase().includes('special');
                  const rate = isSpecial ? specialRate : simpleRate;
                  const comm = Number(svc.price || 0) * rate / 100;
                  return (
                    <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '10px 0', fontSize: '13px', fontWeight: 500 }}>{svc.service_name}</td>
                      <td style={{ padding: '10px 0', fontSize: '12px', color: '#6b7280' }}>{svc.category_name || 'Simple'}</td>
                      <td style={{ padding: '10px 0' }}>
                        <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '4px', background: svc.location === 'HOME_VISIT' ? '#E8F0FF' : '#F3E8FF', color: svc.location === 'HOME_VISIT' ? '#1e40af' : '#7c3aed' }}>
                          {svc.location === 'HOME_VISIT' ? 'Home Visit' : 'On-Site'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 0', fontSize: '13px', fontWeight: 600, textAlign: 'right' }}>IQD {Number(svc.price || 0).toLocaleString()}</td>
                      <td style={{ padding: '10px 0', fontSize: '12px', textAlign: 'right', color: '#EF4444' }}>{rate}% = IQD {comm.toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Session Invoice */}
            <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px' }}>
                <span style={{ color: '#374151' }}>Subtotal</span>
                <span style={{ fontWeight: 500 }}>IQD {session.subtotal.toLocaleString()}</span>
              </div>
              {session.transportationFee > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px' }}>
                  <span style={{ color: '#374151' }}>Transportation Fee</span>
                  <span style={{ fontWeight: 500 }}>IQD {session.transportationFee.toLocaleString()}</span>
                </div>
              )}
              {session.simpleCommission > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px' }}>
                  <span style={{ color: '#6b7280' }}>Simple Commission ({simpleRate}%)</span>
                  <span style={{ color: '#EF4444' }}>- IQD {session.simpleCommission.toLocaleString()}</span>
                </div>
              )}
              {session.specialCommission > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px' }}>
                  <span style={{ color: '#6b7280' }}>Special Commission ({specialRate}%)</span>
                  <span style={{ color: '#EF4444' }}>- IQD {session.specialCommission.toLocaleString()}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 0', marginTop: '6px', borderTop: '1px solid #e5e7eb', fontSize: '14px' }}>
                <span style={{ fontWeight: 600, color: '#065f46' }}>Net Earnings</span>
                <span style={{ fontWeight: 700, color: '#099494' }}>IQD {session.netEarnings.toLocaleString()}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </PhysioDashboardLayout>
  );
};

export default PhysioEarningDetail;
